import { getCurrentScope } from 'vue'
import { useNow } from '@vueuse/core'

/**
 * Composable for accessing the current parade year and date
 *
 * This provides the active parade year/date from Firestore `system/config`,
 * falling back to the runtime config value if not loaded yet.
 *
 * Usage:
 * ```ts
 * const { paradeYear, paradeDate, isParadeDay } = useParadeYear()
 * ```
 */
export function useParadeYear() {
  const systemConfigStore = useSystemConfigStore()

  // Ticks every minute, so "parade day" and the countdown turn over at
  // midnight on a screen left open. Only where a scope (a component) will
  // stop the timer; elsewhere it is the time of the call
  const now = getCurrentScope() ? useNow({ interval: 60_000 }) : ref(new Date())

  /**
   * The effective parade year - from DB if loaded, otherwise from config
   */
  const paradeYear = computed<number>(() => {
    return systemConfigStore.paradeYear
  })

  /**
   * The parade date as a Date object, or null if not configured
   */
  const paradeDate = computed<Date | null>(() => {
    return systemConfigStore.paradeDate
  })

  /**
   * Whether `date` falls on parade day. Decisions (can they check in?) pass
   * `new Date()`: `isParadeDay` can be up to a minute behind
   */
  const isParadeDayAt = (date: Date): boolean => {
    const pd = paradeDate.value
    if (!pd) return false
    return (
      date.getFullYear() === pd.getFullYear()
      && date.getMonth() === pd.getMonth()
      && date.getDate() === pd.getDate()
    )
  }

  /**
   * Whether today is parade day (based on configured paradeDate), for display
   */
  const isParadeDay = computed<boolean>(() => isParadeDayAt(now.value))

  /**
   * Whole days from today until parade day: 0 on the day, negative after it,
   * null when no date is configured
   */
  const daysUntilParade = computed<number | null>(() => {
    const pd = paradeDate.value
    if (!pd) return null
    const today = now.value
    const startOfToday = new Date(today.getFullYear(), today.getMonth(), today.getDate())
    // Rounded so a daylight-saving shift between the two dates doesn't lose a day
    return Math.round((pd.getTime() - startOfToday.getTime()) / 86_400_000)
  })

  /**
   * Whether the config has been loaded from the database
   */
  const isLoaded = computed(() => systemConfigStore.loaded)

  /**
   * Ensure the config is loaded (call this if you need to guarantee DB value).
   * While a load is out, this waits for it
   */
  const ensureLoaded = async (): Promise<number> => {
    if (!systemConfigStore.loaded) {
      await systemConfigStore.loadConfig()
    }
    return systemConfigStore.paradeYear
  }

  return {
    paradeYear,
    paradeDate,
    isParadeDay,
    isParadeDayAt,
    daysUntilParade,
    isLoaded,
    ensureLoaded,
  }
}
